import { useState } from "react";
import { useRoute, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { threadSharingService } from "@/services/threadSharingService";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  ArrowRight,
  Share2,
  Lock,
  MessageSquare,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { buildLoginUrlWithReturnTo } from "@/lib/publicRoutes";

export default function ThreadShareAcceptance() {
  const [, params] = useRoute("/threads/share/:token");
  const [location, setLocation] = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const token = params?.token || "";

  const [hasResponded, setHasResponded] = useState(false);

  const { data: validation, isLoading: isValidating } = useQuery({
    queryKey: ["/api/threads/share/validate", token],
    queryFn: async () => {
      if (!token) return null;
      return await threadSharingService.validateShareLink(token);
    },
    enabled: !!token,
    retry: false,
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      return await threadSharingService.acceptShareInvitation(token);
    },
    onSuccess: () => {
      setHasResponded(true);
      toast({
        title: "Access granted",
        description: "You now have access to this thread",
      });
      setTimeout(() => {
        if (validation?.thread) {
          setLocation(`/chat?threadId=${validation.thread.id}`);
        }
      }, 1500);
    },
    onError: (error: any) => {
      toast({
        title: "Failed to accept",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async () => {
      return await threadSharingService.rejectShareInvitation(token);
    },
    onSuccess: () => {
      setHasResponded(true);
      toast({
        title: "Invitation declined",
        description: "You have declined access to this thread",
      });
      setTimeout(() => {
        setLocation("/threads");
      }, 1500);
    },
    onError: (error: any) => {
      toast({
        title: "Failed to reject",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
    },
  });

  const handleAccept = () => acceptMutation.mutate();
  const handleReject = () => rejectMutation.mutate();

  if (authLoading || isValidating) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-sm text-muted-foreground">Validating share link...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Sign in required
            </CardTitle>
            <CardDescription>
              You need to sign in to access this shared thread
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Authentication required</AlertTitle>
              <AlertDescription>
                Please sign in or create an account to accept this thread invitation.
              </AlertDescription>
            </Alert>
            <Button
              onClick={() => setLocation(buildLoginUrlWithReturnTo(location))}
              className="w-full"
              data-testid="button-go-to-login-thread"
            >
              Go to Sign In
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!validation || !validation.isValid) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <XCircle className="h-5 w-5" />
              Invalid Link
            </CardTitle>
            <CardDescription>
              This share link is not valid or has been deactivated
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Link not valid</AlertTitle>
              <AlertDescription>
                {validation?.message ||
                  "The share link you're trying to access is invalid, expired, or has been revoked."}
              </AlertDescription>
            </Alert>
            <Button
              onClick={() => setLocation("/threads")}
              variant="outline"
              className="w-full"
              data-testid="button-go-to-threads"
            >
              Go to Threads
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { share, thread, access } = validation;
  const isOwner = thread && user.id === share?.createdByUserId;

  if (access?.status === "accepted" || hasResponded) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="h-5 w-5" />
              Access Granted
            </CardTitle>
            <CardDescription>
              You already have access to this thread
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>You have access</AlertTitle>
              <AlertDescription>
                {isOwner
                  ? "This is your own thread. Redirecting..."
                  : "You have already accepted access to this thread. Redirecting..."}
              </AlertDescription>
            </Alert>
            {thread && (
              <Button
                onClick={() => setLocation(`/chat?threadId=${thread.id}`)}
                className="w-full"
                data-testid="button-go-to-thread"
              >
                Go to Thread
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (access?.status === "rejected") {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5" />
              Access Declined
            </CardTitle>
            <CardDescription>
              You have previously declined access to this thread
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Access declined</AlertTitle>
              <AlertDescription>
                You previously declined the invitation to access this thread.
              </AlertDescription>
            </Alert>
            <Button
              onClick={() => setLocation("/threads")}
              variant="outline"
              className="w-full"
              data-testid="button-go-to-threads-rejected"
            >
              Go to Threads
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Thread Invitation
          </CardTitle>
          <CardDescription>
            You've been invited to access a shared thread
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {thread && (
            <div className="space-y-2">
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/50">
                <div className="p-2 rounded-md flex-shrink-0 bg-primary/10">
                  <MessageSquare className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm truncate">
                    {thread.title || "Untitled Chat"}
                  </h3>
                  {thread.topic && (
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                      {thread.topic}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Accept invitation?</AlertTitle>
            <AlertDescription>
              {share?.accessType === "restricted"
                ? "Accepting will grant you access to view this thread."
                : "This thread is shared. Accepting will grant you access."}
            </AlertDescription>
          </Alert>

          {share?.expiresAt && (
            <p className="text-xs text-muted-foreground text-center">
              Link expires {formatDistanceToNow(new Date(share.expiresAt), { addSuffix: true })}
            </p>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={handleReject}
              disabled={acceptMutation.isPending || rejectMutation.isPending || hasResponded}
              className="flex-1"
              data-testid="button-reject-invitation-thread"
            >
              <XCircle className="h-4 w-4 mr-2" />
              Decline
            </Button>
            <Button
              onClick={handleAccept}
              disabled={acceptMutation.isPending || rejectMutation.isPending || hasResponded}
              className="flex-1"
              data-testid="button-accept-invitation-thread"
            >
              {acceptMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Accepting...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Accept
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
